import { useEffect, useState } from 'react';

import { StandaloneProject } from '../../entities/standalone-project';
import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

const storageKey = (userId: string) => `mflow_standalone_project_${userId}`;

const readStored = (userId: string): StandaloneProject | null => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// StandaloneProject has no REST API — the only way to learn a project's real id is the
// `standaloneProjectUpdated` WS event. Persisted to localStorage so a returning user keeps their
// bound project across reloads (there's no way to re-fetch it otherwise).
export function useStandaloneProject() {
  const userId = auth.currentUser?.uid ?? 'anonymous';
  const { on, off } = useSocket(userId);

  const [project, setProject] = useState<StandaloneProject | null>(() => readStored(userId));

  useEffect(() => {
    const onUpdated = (data: { userId: string; project: StandaloneProject }) => {
      setProject(data.project);
      try {
        localStorage.setItem(storageKey(userId), JSON.stringify(data.project));
      } catch {
        // ignore quota errors
      }
    };

    on('standaloneProjectUpdated', onUpdated);
    return () => off('standaloneProjectUpdated', onUpdated as (...args: unknown[]) => void);
  }, [userId, on, off]);

  return { project };
}
